import axios, { AxiosError } from "axios";
import { API_URL, ApiResponse } from "@/api/api.ts";
import { loginState } from "@/states.ts";

type ErrorBody = { errors?: Record<string, string[]> };

const CONNECT_ERROR = "Couldn't reach the server. Check your connection and try again.";
const GENERIC_ERROR = "Something went wrong. Please try again.";

export const authErrorMessage = (e: unknown): string | undefined => {
	if (!(e instanceof AxiosError)) return CONNECT_ERROR;
	const err = e as AxiosError<ErrorBody>;
	if (err.response?.status === 429) {
		return (
			err.response.data?.errors?.rate_limit?.[0] ?? "Too many attempts. Try again later."
		);
	}
	if (err.response?.data?.errors) return undefined;
	if (err.response) return GENERIC_ERROR;
	return CONNECT_ERROR;
};

export interface LoginResponse extends ApiResponse {
	token?: string;
	errors?: {
		email?: string[];
		password?: string[];
	};
}

export const login = async (email: string, password: string) => {
	return axios
		.post<LoginResponse>(
			`${API_URL}/user/login`,
			{
				email,
				password,
			},
			{ headers: { "Content-Type": "application/json" } },
		)
		.then((res) => res.data);
};

export interface RegisterResponse extends ApiResponse {
	token?: string;
	errors?: {
		email?: string[];
		password?: string[];
	};
}

export const register = async (email: string, password: string, password2: string) => {
	return axios
		.post<RegisterResponse>(
			`${API_URL}/user/register`,
			{
				email,
				password,
				password2,
			},
			{ headers: { "Content-Type": "application/json" } },
		)
		.then((res) => res.data);
};

export interface RequestResetResponse extends ApiResponse {
	errors?: {
		email?: string[];
	};
}

export const requestPasswordReset = async (email: string) => {
	return axios
		.post<RequestResetResponse>(
			`${API_URL}/user/request_reset`,
			{
				email,
			},
			{ headers: { "Content-Type": "application/json" } },
		)
		.then((res) => res.data);
};

export interface ResetPasswordResponse extends ApiResponse {
	token?: string;
	errors?: {
		token?: string[];
		password?: string[];
	};
}

export const resetPassword = async (
	token: string,
	uuid: string,
	password: string,
	password2: string,
) => {
	return axios
		.post<ResetPasswordResponse>(
			`${API_URL}/user/verify_reset`,
			{
				token,
				uuid,
				password,
				password2,
			},
			{ headers: { "Content-Type": "application/json" } },
		)
		.then((res) => res.data);
};

export const deleteAccount = async () => {
	return axios
		.delete<ApiResponse>(`${API_URL}/user/`, {
			headers: {
				"Content-Type": "application/json",
				Authorization: loginState.token,
			},
		})
		.then((res) => res.data);
};
